import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    useTheme,
    Avatar,
    Divider,
    Tooltip,
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Settings as SettingsIcon,
    People as PeopleIcon,
    Analytics as AnalyticsIcon,
    BarChart as ChartIcon,
    Notifications as NotificationsIcon,
    Logout as LogoutIcon,
} from '@mui/icons-material';
import ConnectionStatus from './common/ConnectionStatus';
import { useAuth } from '../contexts/AuthContext';

const DRAWER_WIDTH = 280;

interface LayoutProps {
    children: React.ReactNode;
}

function initials(email: string): string {
    const base = email.split('@')[0] || email;
    const parts = base.split(/[._-]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return base.slice(0, 2).toUpperCase();
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout, hasRole } = useAuth();

    const handleLogout = async () => {
        try {
            await logout();
        } finally {
            navigate('/login', { replace: true });
        }
    };

    const menuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
        { text: 'Analytics', icon: <ChartIcon />, path: '/analytics' },
        ...(hasRole('admin')
            ? [{ text: 'Users', icon: <PeopleIcon />, path: '/admin/users' }]
            : []),
        { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    ];

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            {/* Sidebar */}
            <Drawer
                variant="permanent"
                sx={{
                    width: DRAWER_WIDTH,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        border: 'none',
                        backgroundImage: 'none',
                        backgroundColor: theme.palette.background.paper,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                    },
                }}
            >
                <Box>
                    {/* Logo and Title */}
                    <Box
                        sx={{
                            p: 3,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        }}
                    >
                        <Box
                            sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '12px',
                                backgroundColor: 'rgba(58, 134, 255, 0.12)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 2,
                            }}
                        >
                            <Typography
                                variant="h4"
                                sx={{
                                    color: theme.palette.primary.main,
                                    fontWeight: 'bold',
                                }}
                            >
                                M
                            </Typography>
                        </Box>

                        <Typography
                            variant="h6"
                            sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 'bold',
                            }}
                        >
                            MODO Devices
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Control Center
                            </Typography>
                            <ConnectionStatus />
                        </Box>
                    </Box>

                    {/* Navigation Menu */}
                    <List sx={{ p: 2 }}>
                        {menuItems.map((item) => (
                            <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                                <ListItemButton
                                    onClick={() => navigate(item.path)}
                                    selected={location.pathname === item.path || 
                                             (item.path === '/' && location.pathname.startsWith('/device'))}
                                    sx={{
                                        borderRadius: '12px',
                                        py: 1.2,
                                        '&.Mui-selected': {
                                            backgroundColor: theme.palette.mode === 'dark' 
                                                ? 'rgba(58, 134, 255, 0.15)' 
                                                : 'rgba(58, 134, 255, 0.1)',
                                            '&:hover': {
                                                backgroundColor: theme.palette.mode === 'dark'
                                                    ? 'rgba(58, 134, 255, 0.2)' 
                                                    : 'rgba(58, 134, 255, 0.15)',
                                            },
                                        },
                                    }}
                                >
                                    <ListItemIcon 
                                        sx={{ 
                                            color: (location.pathname === item.path || 
                                                  (item.path === '/' && location.pathname.startsWith('/device'))) 
                                                ? theme.palette.primary.main 
                                                : theme.palette.text.secondary,
                                            minWidth: 42,
                                        }}
                                    >
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={item.text}
                                        sx={{
                                            '& .MuiListItemText-primary': {
                                                color: (location.pathname === item.path || 
                                                       (item.path === '/' && location.pathname.startsWith('/device'))) 
                                                    ? theme.palette.primary.main 
                                                    : theme.palette.text.primary,
                                                fontWeight: (location.pathname === item.path || 
                                                            (item.path === '/' && location.pathname.startsWith('/device'))) 
                                                    ? 600 
                                                    : 400,
                                            },
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </Box>

                {/* User Profile Section at Bottom */}
                <Box sx={{ mt: 'auto', p: 2 }}>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between' }}>
                        <Tooltip title="Notifications">
                            <Box 
                                sx={{ 
                                    p: 1, 
                                    borderRadius: '8px', 
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
                                }}
                            >
                                <NotificationsIcon color="action" />
                            </Box>
                        </Tooltip>
                        <Tooltip title="Logout">
                            <Box
                                onClick={handleLogout}
                                sx={{
                                    p: 1,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
                                }}
                            >
                                <LogoutIcon color="action" />
                            </Box>
                        </Tooltip>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            p: 1.5,
                            borderRadius: 2,
                        }}
                    >
                        <Avatar
                            sx={{
                                bgcolor: theme.palette.primary.main,
                                width: 42,
                                height: 42,
                            }}
                        >
                            {user ? initials(user.email) : '?'}
                        </Avatar>
                        <Box sx={{ ml: 2, overflow: 'hidden' }}>
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 500, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                                title={user?.email || ''}
                            >
                                {user?.email || '—'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: theme.palette.primary.main, textTransform: 'capitalize' }}>
                                {user?.role || 'guest'}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Drawer>

            {/* Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    backgroundColor: theme.palette.background.default,
                    minHeight: '100vh',
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

export default Layout; 